interface Cached<T> {
  expiresAt: number;
  value: T;
}

/**
 * Wrap a zero-arg async fetcher so its result is cached in-process for `ttlMs`,
 * and concurrent cold-cache calls share a single in-flight request.
 *
 * This keeps us from hammering an upstream API (e.g. Trustpilot, which has no
 * caching of its own and is called on every page render): each pod makes at
 * most one upstream call per TTL window, and a burst of simultaneous requests
 * on an empty/expired cache collapses into one call rather than a thundering
 * herd — which is precisely the pattern that trips upstream rate limits.
 *
 * Errors are never cached and clear the in-flight promise, so the next call
 * retries the upstream rather than serving a cached failure.
 */
export const withTtlCache = <T>(fetcher: () => Promise<T>, ttlMs: number): (() => Promise<T>) => {
  let cached: Cached<T> | undefined;
  let inFlight: Promise<T> | undefined;

  return () => {
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.value);
    }

    if (inFlight) {
      return inFlight;
    }

    inFlight = fetcher()
      .then((value) => {
        cached = { expiresAt: Date.now() + ttlMs, value };
        return value;
      })
      .finally(() => {
        inFlight = undefined;
      });

    return inFlight;
  };
};
